// queue.repo — akses Supabase untuk Antrian (daftar per status, detail, transisi).
import { supabase } from '@core/supabase';
import { fetchAllPaged, fetchAllChunked } from '@core/paging';
import { masihBerjalan } from '@domain/status';
import { fetchRounds, rowToTicket, MONEY_COLS, type TicketRow } from '@core/ticketMap';
import { paidSoFar, billedNet } from '@domain/money';
import type { PaymentRound, Ticket, Refund } from '@core/types';

export interface QueueRow {
  no: string; status: string; unit: string; keluhan: string;
  teknisi: string | null; prioritas: string; tglISO: string;
  dp: number; estBiaya: number; hold: boolean; custNama: string;
  estSelesai: string | null; kerja: string | null; reconfirm: boolean; servisUlang: boolean; konfirmasi: boolean;
  priorRef: string | null;   // tiket lama asalnya (garansi habis → tiket baru berbayar)
}

interface Raw {
  no: string; status: string; unit: string | null; keluhan: string | null;
  teknisi: string | null; prioritas: string | null; tgl_iso: string | null;
  dp: number | null; est_biaya: number | null; hold: boolean | null; est_selesai: string | null;
  kerja: string | null; reconfirm: boolean | null; servis_ulang: boolean | null; konfirmasi: boolean | null;
  prior_ref: string | null;
  customers: { nama: string } | { nama: string }[] | null;
}

const custName = (c: Raw['customers']): string =>
  (Array.isArray(c) ? c[0]?.nama : c?.nama) ?? '—';

export async function listByStatus(status: string): Promise<QueueRow[]> {
  // Paging: antrian yang menumpuk bisa melewati 1.000 baris, dan yang hilang justru yang paling
  // lama menunggu (urutan tanggal menaruhnya di belakang) — persis unit yang harus terlihat.
  const data = await fetchAllPaged<Raw>((dari, sampai) => supabase()
    .from('tickets')
    .select('no,status,unit,keluhan,teknisi,prioritas,tgl_iso,dp,est_biaya,est_selesai,hold,kerja,reconfirm,servis_ulang,konfirmasi,prior_ref,customers(nama)')
    .eq('status', status)
    .eq('archived', false)
    .order('tgl_iso', { ascending: false })
    .range(dari, sampai));
  return (data as unknown as Raw[]).map((t) => ({
    no: t.no, status: t.status, unit: t.unit ?? '—', keluhan: t.keluhan ?? '—',
    teknisi: t.teknisi, prioritas: t.prioritas ?? 'P2', tglISO: t.tgl_iso ?? '',
    dp: t.dp ?? 0, estBiaya: t.est_biaya ?? 0, hold: t.hold ?? false,
    estSelesai: t.est_selesai, kerja: t.kerja, reconfirm: t.reconfirm ?? false,
    servisUlang: t.servis_ulang ?? false, konfirmasi: t.konfirmasi ?? false, priorRef: t.prior_ref ?? null, custNama: custName(t.customers),
  }));
}

/** Tandai/batal "sudah konfirmasi ke pelanggan" (status Menunggu Persetujuan). Port markKonf/unmarkKonf v0.18. */
export async function setKonfirmasi(no: string, val: boolean, by: string): Promise<void> {
  const { error } = await supabase().from('tickets').update({ konfirmasi: val }).eq('no', no);
  if (error) throw new Error(error.message);
  await supabase().from('ticket_logs').insert({
    ticket_no: no, status: val ? 'Konfirmasi pelanggan' : 'Konfirmasi dibatalkan',
    catatan: val ? 'Keputusan pelanggan telah dicatat petugas' : 'Status → belum konfirmasi', by_user: by,
  });
}

export interface JasaPrice { id: number; nama: string; toko: number; user: number; harga_type_toko: 'rp' | 'persen'; harga_type_user: 'rp' | 'persen' }
/** Daftar Jasa + harga efektif (override cabang bila ada) — untuk "Tambah dari daftar Jasa" di Hasil Pengerjaan.
 *  toko/user = Rp ('rp') atau PERSEN markup ('persen'), satuan PER-FIELD (harga_type_toko / harga_type_user). */
export async function listJasaPrices(outlet: string): Promise<JasaPrice[]> {
  const [s, o] = await Promise.all([
    supabase().from('services').select('id,nama,toko,user,harga_type_toko,harga_type_user').order('nama'),
    supabase().from('service_overrides').select('service_id,toko,user').eq('outlet', outlet),
  ]);
  if (s.error) throw new Error(s.error.message);
  const ovr = (o.data ?? []) as { service_id: number; toko: number; user: number }[];
  return ((s.data ?? []) as { id: number; nama: string; toko: number; user: number; harga_type_toko: 'rp' | 'persen' | null; harga_type_user: 'rp' | 'persen' | null }[]).map((x) => {
    const ov = ovr.find((v) => v.service_id === x.id);
    return { id: x.id, nama: x.nama, toko: ov?.toko ?? x.toko, user: ov?.user ?? x.user, harga_type_toko: x.harga_type_toko ?? 'rp', harga_type_user: x.harga_type_user ?? 'rp' };
  });
}

/** Status tiket saat ini (utk guard idempoten transisi). */
export async function currentStatus(no: string): Promise<string> {
  const { data, error } = await supabase().from('tickets').select('status').eq('no', no).single();
  if (error) throw new Error(error.message);
  return (data as { status: string }).status;
}

/** Status + teknisi + outlet + kerja — untuk guard transisi RBAC (P1-c) + preserve kerja saat approve. */
export async function guardInfo(no: string): Promise<{ status: string; teknisi: string | null; outlet: string | null; kerja: string | null }> {
  const { data, error } = await supabase().from('tickets').select('status,teknisi,outlet,kerja').eq('no', no).single();
  if (error) throw new Error(error.message);
  return data as { status: string; teknisi: string | null; outlet: string | null; kerja: string | null };
}

/** Jumlah tiket per status (non-arsip, RLS-scoped) — badge angka di sidebar. Port countStatus v0.18. */
export async function statusCounts(): Promise<Record<string, number>> {
  // Badge angka di sidebar: kalau terpotong, angkanya diam-diam mengecil dan petugas mengira
  // pekerjaannya sudah habis.
  const data = await fetchAllPaged<{ status: string }>((dari, sampai) => supabase()
    .from('tickets').select('status').eq('archived', false).order('no').range(dari, sampai));
  const m: Record<string, number> = {};
  for (const r of data) m[r.status] = (m[r.status] ?? 0) + 1;
  return m;
}

/** Map no → ISO waktu tiket MASUK status berjalan (log terbaru berstatus tsb) — basis SLA. */
/**
 * Tambahkan foto kondisi unit ke tiket. Ditulis SAAT foto diambil (bukan saat serah selesai):
 * fotonya bukti keadaan unit pada detik itu, dan kalau petugas membatalkan langkah pembayaran
 * buktinya tetap tersimpan — persis saat paling mungkin dipersoalkan.
 */
export async function tambahFotoUnit(no: string, tambahan: { path: string; fase: 'terima' | 'serah'; at: string; by: string }[]): Promise<void> {
  if (!tambahan.length) return;
  const sb = supabase();
  const { data, error } = await sb.from('tickets').select('foto_unit').eq('no', no).single();
  if (error) throw new Error(error.message);
  const lama = ((data as { foto_unit?: unknown[] } | null)?.foto_unit ?? []) as typeof tambahan;
  const { error: e2 } = await sb.from('tickets').update({ foto_unit: [...lama, ...tambahan].slice(0, 12) }).eq('no', no);
  if (e2) throw new Error(e2.message);
}

export async function statusEnterMap(nos: string[], status: string): Promise<Map<string, string>> {
  const m = new Map<string, string>();
  if (!nos.length) return m;
  const data = await fetchAllChunked<{ ticket_no: string; at: string }>(nos, (bagian, a, b) => supabase()
    .from('ticket_logs').select('ticket_no,at')
    .in('ticket_no', bagian).eq('status', status).order('at').range(a, b)); // asc → penulisan terakhir = terbaru
  for (const r of data) m.set(r.ticket_no, r.at);
  return m;
}

export interface RoundDisplay {
  ronde: number; tindakan: string | null; hasil: string | null; biaya: number | null;
  garansi: string | null; teknisi: string | null; lunas: boolean | null; verified: boolean | null;
  garansiBase?: string | null; // base garansi sesi lama (dari data jsonb) → utk pewarisan garansi servis-ulang
}
export interface TicketFull {
  ticket: Record<string, unknown>;
  logs: { status: string; catatan: string; by_user: string; at: string }[];
  rounds: RoundDisplay[];
  custNama: string;
}

export async function getTicket(no: string): Promise<TicketFull> {
  const [tRes, lRes, rRes] = await Promise.all([
    supabase().from('tickets').select('*, customers(nama,wa,kat)').eq('no', no).single(),
    supabase().from('ticket_logs').select('status,catatan,by_user,at').eq('ticket_no', no).order('at'),
    supabase().from('ticket_rounds').select('ronde,tindakan,hasil,biaya,garansi,teknisi,lunas,verified,data').eq('ticket_no', no).order('ronde'),
  ]);
  if (tRes.error) {
    throw new Error(tRes.error.code === 'PGRST116'
      ? `Tiket ${no} tidak bisa dibuka — di luar cakupan akses Anda.`
      : tRes.error.message);
  }
  const logs = lRes.data, rounds = rRes.data;
  const t = tRes.data as Record<string, unknown> & { customers?: { nama: string } | { nama: string }[] | null };
  return {
    ticket: t,
    logs: (logs ?? []) as TicketFull['logs'],
    rounds: ((rounds ?? []) as (RoundDisplay & { data?: { garansi_base?: string | null; tgl_ambil_iso?: string | null } | null })[])
      .map((r) => ({ ...r, garansiBase: (r.data?.garansi_base ?? r.data?.tgl_ambil_iso) ?? null })),
    custNama: custName(t.customers ?? null),
  };
}

/** Base garansi tiket saat ini (utk preserve saat pickup — jangan timpa warisan servis-ulang). */
export async function garansiBaseOf(no: string): Promise<string | null> {
  const { data, error } = await supabase().from('tickets').select('garansi_base').eq('no', no).single();
  if (error) throw new Error(error.message);
  return (data as { garansi_base: string | null }).garansi_base;
}

/** Meta utk edit garansi (garansi lama + basis + tgl ambil). */
export async function garansiEditMeta(no: string): Promise<{ garansi: string | null; garansiBase: string | null; tglAmbilISO: string | null; frozen: boolean }> {
  const { data, error } = await supabase().from('tickets').select('garansi,garansi_base,tgl_ambil_iso,archived,voided').eq('no', no).single();
  if (error) throw new Error(error.message);
  const r = data as { garansi: string | null; garansi_base: string | null; tgl_ambil_iso: string | null; archived: boolean | null; voided: boolean | null };
  return { garansi: r.garansi, garansiBase: r.garansi_base, tglAmbilISO: r.tgl_ambil_iso, frozen: !!r.archived || !!r.voided };
}

/** Nama jasa "dugaan tindakan" (diisi CS saat intake) — ditampilkan ke teknisi di dialog Diagnosa.
 *  maybeSingle(): dugaan bisa kosong / jasa bisa terhapus → 0-baris = null (SAH), bukan error.
 *  Source: postgrest-js JSDoc — "maybeSingle: zero or one row ... returns null when given 0 rows". */
export async function dugaanNama(no: string): Promise<string | null> {
  const { data: t } = await supabase().from('tickets').select('dugaan').eq('no', no).maybeSingle();
  const id = (t as { dugaan: number | null } | null)?.dugaan;
  if (!id) return null;
  const { data: s } = await supabase().from('services').select('nama').eq('id', id).maybeSingle();
  return (s as { nama: string } | null)?.nama ?? null;
}

/** Kas dibayar pelanggan lintas sesi (reuse canonical paidSoFar) — default usul refund di Hasil Pengerjaan. */
export async function paidSoFarOf(no: string): Promise<number> {
  const { data, error } = await supabase().from('tickets').select(MONEY_COLS).eq('no', no).single();
  if (error) throw new Error(error.message);
  const rounds = (await fetchRounds([no])).get(no) ?? [];
  return paidSoFar(rowToTicket(data as TicketRow, rounds));
}

/** Metadata ringkas utk notifikasi (scoping cabang + target teknisi). */
export async function ticketMeta(no: string): Promise<{ outlet: string | null; teknisi: string | null; status: string }> {
  const { data, error } = await supabase().from('tickets').select('outlet,teknisi,status').eq('no', no).single();
  if (error) throw new Error(error.message);
  const r = data as { outlet: string | null; teknisi: string | null; status: string };
  return { outlet: r.outlet, teknisi: r.teknisi, status: r.status };
}

/** Waktu (ISO) tiket terakhir MASUK status 'Siap Diambil' — basis biaya simpan. */
export async function siapSinceISO(no: string): Promise<string | null> {
  const { data, error } = await supabase()
    .from('ticket_logs').select('at')
    .eq('ticket_no', no).eq('status', 'Siap Diambil')
    .order('at', { ascending: false }).limit(1);
  if (error) throw new Error(error.message);
  return (data?.[0] as { at: string } | undefined)?.at ?? null;
}

export async function listTechnicians(outlet: string): Promise<string[]> {
  const { data, error } = await supabase()
    .from('employees').select('nama')
    .eq('role', 'Teknisi').eq('outlet', outlet).eq('aktif', true).order('nama');
  if (error) throw new Error(error.message);
  return ((data ?? []) as { nama: string }[]).map((e) => e.nama);
}

/** Update field tiket + set status baru + catat log. */
export async function advance(
  no: string, patch: Record<string, unknown>, newStatus: string, logCatatan: string, by: string,
): Promise<void> {
  const { error } = await supabase().from('tickets').update({ ...patch, status: newStatus }).eq('no', no);
  if (error) throw new Error(error.message);
  const { error: e2 } = await supabase()
    .from('ticket_logs').insert({ ticket_no: no, status: newStatus, catatan: logCatatan, by_user: by });
  if (e2) throw new Error(e2.message);
}

/** Update field tanpa mengubah status (mis. prioritas/hold) + catat log. */
export async function updateFields(
  no: string, fields: Record<string, unknown>, logStatus: string, logCatatan: string, by: string,
): Promise<void> {
  const { error } = await supabase().from('tickets').update(fields).eq('no', no);
  if (error) throw new Error(error.message);
  const { error: e2 } = await supabase()
    .from('ticket_logs').insert({ ticket_no: no, status: logStatus, catatan: logCatatan, by_user: by });
  if (e2) throw new Error(e2.message);
}

// removeTicket (hard-delete) DIHAPUS di §26 v20 — dilarang di level DB (RLS restrictive) & alur lewat approval.

interface SnapRow {
  ronde: number | null; keluhan: string | null; tindakan: string | null; kondisi: string | null; hasil: string | null;
  biaya: number | null; modal: number | null; dp: number | null; lunas: boolean | null; verified: boolean | null;
  biaya_simpan: number | null; garansi: string | null; teknisi: string | null; cara_bayar: string | null;
  refund: unknown; pengambil: string | null; tgl_ambil_iso: string | null; garansi_base: string | null; diwakilkan: unknown;
  lunas_at: string | null; lunas_via: string | null; dp_via: string | null; tgl_iso: string | null; jatuh_tempo: string | null;
  foto_unit: unknown;
}

/** Servis-ulang (garansi valid): bekukan ronde berjalan ke ticket_rounds, reset tiket → Dikerjakan sesi baru (garansi diwarisi). */
export async function servisUlang(no: string, keluhan: string, by: string): Promise<void> {
  const { data, error } = await supabase().from('tickets')
    .select('ronde,keluhan,tindakan,kondisi,hasil,biaya,modal,dp,lunas,verified,biaya_simpan,garansi,teknisi,cara_bayar,refund,pengambil,tgl_ambil_iso,garansi_base,diwakilkan,lunas_at,lunas_via,dp_via,tgl_iso,jatuh_tempo,foto_unit')
    .eq('no', no).single();
  if (error) throw new Error(error.message);
  const t = data as unknown as SnapRow;
  const ronde = Number(t.ronde ?? 1);
  const { error: e1 } = await supabase().from('ticket_rounds').insert({
    ticket_no: no, ronde, keluhan: t.keluhan, tindakan: t.tindakan, kondisi: t.kondisi, hasil: t.hasil,
    biaya: t.biaya, modal: t.modal, dp: t.dp, lunas: t.lunas, verified: t.verified, biaya_simpan: t.biaya_simpan,
    garansi: t.garansi, teknisi: t.teknisi, cara_bayar: t.cara_bayar, refund: t.refund,
    // Arsip per-sesi. Jejak UANG (kapan & via apa) WAJIB ikut: tanpa ini uang sesi lama tak bisa
    // ditempatkan di hari yang benar saat Tutup Kasir — persis bug yang ditemukan Apo.
    data: { pengambil: t.pengambil, tgl_ambil_iso: t.tgl_ambil_iso, garansi_base: t.garansi_base, diwakilkan: t.diwakilkan,
      lunas_at: t.lunas_at, lunas_via: t.lunas_via, dp_via: t.dp_via, tgl_iso: t.tgl_iso, jatuh_tempo: t.jatuh_tempo,
      // Foto sesi lama ikut diarsipkan: itu bukti kondisi unit saat sesi itu, dan sesi baru
      // dimulai dengan foto barunya sendiri. Tanpa ini fotonya jadi yatim lalu bisa terhapus.
      foto_unit: t.foto_unit },
  });
  if (e1) throw new Error(e1.message);
  const { error: e2 } = await supabase().from('tickets').update({
    ronde: ronde + 1, status: 'Dikerjakan', kerja: 'belum', servis_ulang: true, garansi_klaim: true,
    keluhan, biaya: 0, modal: 0, dp: 0, hasil: null, tindakan: null, cara_bayar: null,
    lunas: false, verified: false, biaya_simpan: 0, pengambil: null, tgl_ambil_iso: null, refund: null, refund_usul: null, diwakilkan: null,
    est_biaya: 0, jatuh_tempo: null, reconfirm: false, est_biaya_lama: null, ket_ulang: null, garansi_base: null, foto_unit: [],
  }).eq('no', no);
  if (e2) throw new Error(e2.message);
  const { error: e3 } = await supabase().from('ticket_logs')
    .insert({ ticket_no: no, status: 'Dikerjakan', catatan: `KLAIM GARANSI / servis ulang (sesi ${ronde + 1}): ${keluhan}`, by_user: by });
  if (e3) throw new Error(e3.message);
}

interface PriorRow { customer_id: number; outlet: string; jenis: string | null; merk: string | null; model: string | null; sn: string | null; warna: string | null; unit: string | null }

/**
 * Servis yang MASIH BERJALAN untuk UNIT FISIK yang sama (bukan sekadar turunan satu tiket).
 *
 * Kenapa per-unit: HP yang sudah beberapa kali servis punya BANYAK tiket lama "Sudah Diambil",
 * masing-masing dengan tombol 🔄 sendiri. Menjaga per-tiket-acuan saja masih meloloskan
 * kembar — cukup klik 🔄 di baris riwayat yang lain.
 *
 * Pencocokan unit sama dengan `unitHistory`: pelanggan sama DAN (SN sama, atau merk+tipe sama).
 */
export interface UnitRef { customer_id: number; sn?: string | null; merk?: string | null; model?: string | null }

/** Inti pencocokan unit — dipakai tombol 🔄 (lewat nomor acuan) DAN Terima Servis (sebelum tiket ada). */
export async function activeSameUnit(ref: UnitRef, kecuali?: string): Promise<{ no: string; status: string; penerima: string | null; created_at: string } | null> {
  let q = supabase().from('tickets')
    .select('no,status,archived,sn,merk,model,penerima,created_at')
    // Saring di server: pelanggan Toko bisa punya ratusan tiket lama, dan pemeriksaan ini
    // berjalan tepat saat staf menekan Simpan di jaringan counter yang lemot.
    .eq('customer_id', ref.customer_id).neq('status', 'Sudah Diambil');
  if (kecuali) q = q.neq('no', kecuali);
  const { data, error } = await q.order('created_at', { ascending: true }); // paling awal = tiket yang benar utk dilanjutkan
  if (error) throw new Error(error.message);
  const sn = (ref.sn ?? '').trim().toLowerCase();
  const mm = ((ref.merk ?? '') + (ref.model ?? '')).toLowerCase();
  type R = { no: string; status: string; archived: boolean | null; sn: string | null; merk: string | null; model: string | null; penerima: string | null; created_at: string };
  const rows = (data ?? []) as R[];
  const unitSama = (t: R): boolean =>
    (!!sn && (t.sn ?? '').trim().toLowerCase() === sn) || (!!mm && ((t.merk ?? '') + (t.model ?? '')).toLowerCase() === mm);
  const hit = rows.find((t) => unitSama(t) && masihBerjalan(t.status, !!t.archived));
  return hit ? { no: hit.no, status: hit.status, penerima: hit.penerima, created_at: hit.created_at } : null;
}

export async function activeForUnit(refNo: string): Promise<{ no: string; status: string } | null> {
  const { data: refD, error: e0 } = await supabase().from('tickets')
    .select('customer_id,sn,merk,model').eq('no', refNo).single();
  if (e0) throw new Error(e0.message);
  return activeSameUnit(refD as UnitRef, refNo);
}

/** Servis baru dari unit lama (garansi habis) → tiket BARU berbayar, tautkan prior_ref. */
export async function createFromPrior(oldNo: string, no: string, token: string, keluhan: string, tglISO: string, by: string): Promise<void> {
  const { data, error } = await supabase().from('tickets')
    .select('customer_id,outlet,jenis,merk,model,sn,warna,unit').eq('no', oldNo).single();
  if (error) throw new Error(error.message);
  const o = data as unknown as PriorRow;
  const { error: e1 } = await supabase().from('tickets').insert({
    no, token, customer_id: o.customer_id, outlet: o.outlet, status: 'Diterima',
    jenis: o.jenis, merk: o.merk, model: o.model, sn: o.sn, warna: o.warna, unit: o.unit,
    keluhan, prior_ref: oldNo, penerima: by, tgl_iso: tglISO,
  });
  // 23505 = bentrok index anti-kembar (dua petugas menekan berbarengan) → kalimat manusiawi,
  // bukan pesan mentah database.
  if (e1) throw new Error((e1 as { code?: string }).code === '23505'
    ? 'Unit ini sudah punya servis berjalan — muat ulang halaman dan lanjutkan tiket itu.'
    : e1.message);
  await supabase().from('ticket_logs')
    .insert({ ticket_no: no, status: 'Diterima', catatan: `Servis baru dari ${oldNo} (garansi habis) — berbayar`, by_user: by });
}

export const roundsFor = (no: string): Promise<PaymentRound[]> => fetchRounds([no]).then((m) => m.get(no) ?? []);

export interface UnitHistRow { no: string; tglMasuk: string | null; tglAmbil: string | null; tindakan: string; garansi: string; status: string; hasil: string | null; biaya: number; isRef: boolean }
/** Riwayat servis unit yang sama (pelanggan + SN, atau merk+model) — read-only. RLS-scoped. Port riwayatUnitDialog v0.18. */
export async function unitHistory(no: string): Promise<{ custNama: string; unit: string; rows: UnitHistRow[] }> {
  const { data: refD, error: e0 } = await supabase().from('tickets').select('customer_id,sn,merk,model,unit,customers(nama)').eq('no', no).single();
  if (e0) throw new Error(e0.message);
  const ref = refD as { customer_id: number; sn: string | null; merk: string | null; model: string | null; unit: string | null; customers: { nama: string } | { nama: string }[] | null };
  const { data, error } = await supabase().from('tickets')
    .select(`${MONEY_COLS},tindakan,kondisi,garansi,merk,model,sn`).eq('customer_id', ref.customer_id).order('tgl_iso', { ascending: false });
  if (error) throw new Error(error.message);
  const sn = (ref.sn ?? '').trim().toLowerCase(), mm = ((ref.merk ?? '') + (ref.model ?? '')).toLowerCase();
  const all = (data ?? []) as unknown as (TicketRow & { tindakan: string | null; kondisi: string | null; garansi: string | null; merk: string | null; model: string | null; sn: string | null })[];
  const matched = all.filter((t) => (sn && (t.sn ?? '').trim().toLowerCase() === sn) || ((t.merk ?? '') + (t.model ?? '')).toLowerCase() === mm);
  const rounds = await fetchRounds(matched.map((t) => t.no));
  const rows: UnitHistRow[] = matched.map((t) => ({
    no: t.no, tglMasuk: t.tgl_iso ?? null, tglAmbil: t.tgl_ambil_iso ?? null,
    tindakan: t.tindakan ?? t.kondisi ?? '-', garansi: t.garansi ?? '-', status: t.status, hasil: t.hasil ?? null,
    biaya: billedNet(rowToTicket(t, rounds.get(t.no) ?? [])), isRef: t.no === no,
  }));
  return { custNama: cn(ref.customers), unit: ref.unit ?? `${ref.merk ?? ''} ${ref.model ?? ''}`.trim(), rows };
}

// ===== Daftar kaya: Siap Diambil & Sudah Diambil (port renderSiapDiambil/renderSudahDiambil v0.18) =====
const cn = (c: { nama: string } | { nama: string }[] | null | undefined): string =>
  (Array.isArray(c) ? c[0]?.nama : c?.nama) ?? '—';

export interface SiapRow {
  no: string; tglISO: string | null; custNama: string; unit: string; keluhan: string;
  hasil: string | null; kondisi: string | null; teknisi: string | null; dp: number;
  biaya: number; estBiaya: number; refund: Refund | null; siapISO: string | null; priorRef: string | null;
}
interface SiapRaw {
  no: string; tgl_iso: string | null; unit: string | null; keluhan: string | null;
  hasil: string | null; kondisi: string | null; teknisi: string | null; dp: number | null;
  biaya: number | null; est_biaya: number | null; refund: unknown; prior_ref: string | null;
  customers: { nama: string } | { nama: string }[] | null;
}
export async function listSiap(): Promise<SiapRow[]> {
  const rows = await fetchAllPaged<SiapRaw>((a, b) => supabase().from('tickets')
    .select('no,tgl_iso,unit,keluhan,hasil,kondisi,teknisi,dp,biaya,est_biaya,refund,prior_ref,customers(nama)')
    .eq('status', 'Siap Diambil').eq('archived', false)
    .order('tgl_iso', { ascending: false }).order('no').range(a, b)) as unknown as SiapRaw[];
  const enter = await statusEnterMap(rows.map((r) => r.no), 'Siap Diambil');
  return rows.map((r) => ({
    no: r.no, tglISO: r.tgl_iso, custNama: cn(r.customers), unit: r.unit ?? '—', keluhan: r.keluhan ?? '—',
    hasil: r.hasil, kondisi: r.kondisi, teknisi: r.teknisi, dp: r.dp ?? 0, biaya: r.biaya ?? 0,
    estBiaya: r.est_biaya ?? 0, refund: (r.refund ?? null) as Refund | null, siapISO: enter.get(r.no) ?? null,
    priorRef: r.prior_ref ?? null,
  }));
}

export interface SudahRow {
  no: string; tglISO: string | null; tglAmbilISO: string | null; custNama: string; unit: string; keluhan: string; sn: string;
  hasil: string | null; kondisi: string | null; tindakan: string | null; teknisi: string | null;
  garansi: string | null; garansiBase: string | null; servisUlang: boolean; roundsCount: number; priorRef: string | null;
  lunas: boolean; verified: boolean; caraBayar: string | null; refund: Refund | null; t: Ticket;
}
interface SudahRaw extends TicketRow {
  unit: string | null; keluhan: string | null; sn: string | null; kondisi: string | null; tindakan: string | null;
  garansi: string | null; garansi_base: string | null; servis_ulang: boolean | null; prior_ref: string | null;
  customers: { nama: string } | { nama: string }[] | null;
}
export async function listSudah(): Promise<SudahRow[]> {
  const rows = await fetchAllPaged<SudahRaw>((a, b) => supabase().from('tickets')
    .select(`${MONEY_COLS},unit,keluhan,sn,kondisi,tindakan,garansi,garansi_base,servis_ulang,prior_ref,customers(nama)`)
    .eq('status', 'Sudah Diambil').eq('archived', false).order('no').range(a, b)) as unknown as SudahRaw[];
  const rounds = await fetchRounds(rows.map((r) => r.no));
  return rows.map((r) => {
    const rr = rounds.get(r.no) ?? [];
    return {
      no: r.no, tglISO: r.tgl_iso ?? null, tglAmbilISO: r.tgl_ambil_iso ?? null, custNama: cn(r.customers),
      unit: r.unit ?? '—', keluhan: r.keluhan ?? '—', sn: r.sn ?? '', hasil: r.hasil ?? null, kondisi: r.kondisi ?? null,
      tindakan: r.tindakan ?? null, teknisi: r.teknisi ?? null, garansi: r.garansi ?? null,
      garansiBase: r.garansi_base ?? null, servisUlang: r.servis_ulang ?? false, roundsCount: rr.length,
      priorRef: r.prior_ref ?? null,
      lunas: r.lunas ?? false, verified: r.verified ?? false, caraBayar: r.cara_bayar ?? null,
      refund: (r.refund ?? null) as Refund | null, t: rowToTicket(r, rr),
    };
  });
}
